'use client';

import Link from 'next/link';

import { Button, IconButton, Stack } from '@backyard-ui/core';
import {
  ChevronDoubleLeftOutline,
  ChevronLeftOutline,
  ChevronDoubleRightOutline,
  ChevronRightOutline,
} from '@backyard-ui/icons';

import usePagination from './usePagination';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
}

export default function Pagination(props: Readonly<PaginationProps>) {
  const { currentPage, totalPages } = props;

  const {
    firstPage,
    lastPage,
    isFirstPage,
    isLastPage,
    previousPage,
    nextPage,
    pages,
  } = usePagination({ currentPage, totalPages });

  return (
    <Stack justify="center" spacing={1}>
      {!isFirstPage && (
        <>
          <Link href={{ query: { page: firstPage } }} data-testid="first-page">
            <IconButton
              size="xs"
              radius="lg"
              icon={<ChevronDoubleLeftOutline />}
              variant="ghost"
            />
          </Link>
          <Link
            href={{ query: { page: previousPage } }}
            data-testid="previous-page"
          >
            <IconButton
              size="xs"
              radius="lg"
              icon={<ChevronLeftOutline />}
              variant="ghost"
            />
          </Link>
        </>
      )}

      {pages.map((page, idx) => {
        if (typeof page === 'string') {
          return <span key={idx}>...</span>;
        }

        return (
          <Button
            key={idx}
            size="xs"
            variant={page === currentPage ? 'solid' : 'ghost'}
            data-testid={`page-${page}`}
            asChild
          >
            <Link href={{ query: { page } }}>{page}</Link>
          </Button>
        );
      })}

      {!isLastPage && (
        <>
          <Link href={{ query: { page: nextPage } }} data-testid="next-page">
            <IconButton
              size="xs"
              radius="lg"
              icon={<ChevronRightOutline />}
              variant="ghost"
            />
          </Link>
          <Link href={{ query: { page: lastPage } }} data-testid="last-page">
            <IconButton
              size="xs"
              radius="lg"
              icon={<ChevronDoubleRightOutline />}
              variant="ghost"
            />
          </Link>
        </>
      )}
    </Stack>
  );
}
